import React, { useEffect, useState } from 'react';
import { Alert, AlertIcon, Box, Button, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { api } from '../../../services/api';
import { useActionToast } from '../../../services/actionToast';
import { DataSkeleton } from '../../states';
import { passwordConfirmationField, passwordField } from '../../../utils/passwordValidation';
import { FormButton } from '../../buttons/FormButton';
import { PasswordInputForm } from '../../inputs/forms/PasswordInputForm';

const schema = Yup.object().shape({
  new_password: passwordField,
  confirmation: passwordConfirmationField,
});

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);
  const toast = useActionToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: { new_password: '', confirmation: '' },
  });

  useEffect(() => {
    let active = true;
    const validateToken = async () => {
      if (!token) {
        setTokenState('invalid');
        return;
      }
      try {
        await api.post('/auth/validate-reset-token', { token });
        if (active) {
          setTokenState('valid');
        }
      } catch (error) {
        if (active) {
          setTokenState('invalid');
        }
      }
    };
    validateToken();
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (values: any) => {
    setServerError('');
    try {
      await api.post('/auth/reset-password', { token, ...values });
      setSuccess(true);
      toast({
        status: 'success',
        title: 'Senha redefinida',
        description: 'Use a nova senha para entrar novamente.',
      });
    } catch {
      const feedback =
        'Não foi possível redefinir a senha. Solicite um novo link ou tente novamente.';
      setServerError(feedback);
      toast({ status: 'error', title: 'Senha não redefinida', description: feedback });
    }
  };

  const onInvalid = () => {
    toast({
      status: 'error',
      title: 'Revise o formulário',
      description: 'Corrija os campos indicados antes de enviar.',
      id: 'reset-password-form-invalid',
    });
  };

  if (tokenState === 'loading') {
    return <DataSkeleton variant="form" rows={2} label="Validando link de redefinição" />;
  }

  if (tokenState === 'invalid') {
    return (
      <Box>
        <Alert status="error" borderRadius="md" data-testid="invalid-reset-token">
          <AlertIcon />O link é inválido, expirou ou já foi usado.
        </Alert>
        <Button as={Link} to="/recover" mt={6} width="100%">
          Solicitar outro link
        </Button>
      </Box>
    );
  }

  if (success) {
    return (
      <Box data-testid="reset-success">
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          Senha redefinida. Entre novamente.
        </Alert>
        <Button as={Link} to="/login" mt={6} width="100%" colorScheme="orange">
          Ir para o login
        </Button>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
      <PasswordInputForm
        name="new_password"
        label="Nova senha"
        labelStyles={{ color: 'white' }}
        register={register}
        errors={errors}
        isRequired
        autoComplete="new-password"
      />
      <PasswordInputForm
        name="confirmation"
        label="Confirmar nova senha"
        labelStyles={{ color: 'white' }}
        register={register}
        errors={errors}
        isRequired
        autoComplete="new-password"
      />
      {serverError && (
        <Text color="red.100" mt={3} role="alert">
          {serverError}
        </Text>
      )}
      <FormButton mt={6} width="100%" type="submit" isLoading={isSubmitting}>
        Redefinir senha
      </FormButton>
    </form>
  );
}
